/**
 * Sightseeing Spot Image Resolver Engine
 * Fallback Chain: Wikidata / Wikipedia → Mapillary Graph API → Category Placeholder
 * Features:
 * - Multi-tier caching (In-Memory L1 + LocalStorage L2)
 * - Concurrency queue limiter (Max 5 parallel active network queries)
 * - Strict 5-second fetch timeout with AbortController
 * - Modular, testable pure functions
 */

import { Spot, SpotCategory } from '../types/spot';

// ─── Constants & Configuration ───
export const FETCH_TIMEOUT_MS = 5000;
export const MAX_CONCURRENT_REQUESTS = 5;
export const WIKIDATA_MAX_COORDINATE_DISTANCE_METERS = 2000; // 2km radius to avoid false positives

export const CATEGORY_PLACEHOLDERS: Record<string, string> = {
  temple: '/assets/placeholders/placeholder-temple.svg',
  viewpoint: '/assets/placeholders/placeholder-view.svg',
  cafe: '/assets/placeholders/placeholder-cafe.svg',
  park: '/assets/placeholders/placeholder-park.svg',
  museum: '/assets/placeholders/placeholder-museum.svg',
  activity: '/assets/placeholders/placeholder-park.svg',
  default: '/assets/placeholders/placeholder-default.svg',
};

// Curated modern high-resolution authentic travel photography (Instagram & Travel Blog aesthetic)
export const CURATED_AUTHENTIC_PHOTOS: Record<string, string> = {
  // Fitness & Gyms (Authentic location photos)
  '짐박스피트니스 강남1호점': '/spots/fitness-gymboxx-gangnam-1.jpg',
  '짐박스피트니스 강남2호점': '/spots/fitness-gymboxx-gangnam-2.jpg',
  '스포애니 강남역1호점': '/spots/fitness-spoany-gangnam-1.jpg',
  '에이블짐 강남역점': '/spots/fitness-ablegym-gangnam.jpg',
  '뚝섬한강공원 야외철봉지구': '/spots/fitness-outdoor-ttukseom-hangang.jpg',
  '뚝섬한강공원 체력단련장': '/spots/fitness-outdoor-ttukseom-hangang.jpg',
  '여의도한강공원 체력단련장': '/spots/fitness-outdoor-yeouido-hangang.jpg',
  '남산 장충체육회 산스장': '/spots/fitness-outdoor-namsan-sanseujang.jpg',
  '남산 산스장': '/spots/fitness-outdoor-namsan-sanseujang.jpg',
  '서울숲 야외체육시설': '/spots/fitness-outdoor-seoul-forest.jpg',
  '서울숲 야외운동마당': '/spots/fitness-outdoor-seoul-forest.jpg',
  '반포한강공원 달빛광장 야외운동장': '/spots/fitness-outdoor-banpo-hangang.jpg',
  '반포한강공원 체력단련장': '/spots/fitness-outdoor-banpo-hangang.jpg',
  '송도 글로벌파크 야외운동시설': '/spots/fitness-outdoor-songdo-global-park.jpg',
  '양화 체력단련장1 (양화대교 남단)': '/spots/fitness-outdoor-yanghwa-hangang.jpg',
  '양화 체력단련장1': '/spots/fitness-outdoor-yanghwa-hangang.jpg',
  '양화한강공원 체력단련장': '/spots/fitness-outdoor-yanghwa-hangang.jpg',
  '스몹 스타필드 고양': '/spots/fitness-indoor-smob-sports-monster.jpg',
  '스포츠몬스터 고양': '/spots/fitness-indoor-smob-sports-monster.jpg',
  '스몹 고양': '/spots/fitness-indoor-smob-sports-monster.jpg',
  '스포츠몬스터': '/spots/fitness-indoor-smob-sports-monster.jpg',
  '부평공원 야외체력단련장': '/spots/fitness-outdoor-incheon-bupyeong-park.jpg',
  // Incheon
  '인천 차이나타운': 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1000&auto=format&fit=crop&q=85',
  '송월동 동화마을': 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  '월미도 / 월미테마파크': 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1000&auto=format&fit=crop&q=85',
  '월미도': 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1000&auto=format&fit=crop&q=85',
  '송도 센트럴파크': 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1000&auto=format&fit=crop&q=85',
  'G타워 전망대': 'https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?w=1000&auto=format&fit=crop&q=85',
  '인천개항장 근대역사문화거리': 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1000&auto=format&fit=crop&q=85',
  '자유공원': 'https://images.unsplash.com/photo-1557180295-76eee20ae8aa?w=1000&auto=format&fit=crop&q=85',
  '부평 모두몰 (지하상가)': 'https://images.unsplash.com/photo-1546874177-9e664107314e?w=1000&auto=format&fit=crop&q=85',
  '영종씨사이드파크 레일바이크': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '인천대공원': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '월미산 전망대': '/spots/incheon-wolmi-observatory-tower.jpg',
  '인하대': 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1000&auto=format&fit=crop&q=85',
  '송도 트리플스트리트': 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  '트리플스트리트': 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  '코스모40': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '개항로 거리 & 브라운핸즈 개항로': 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1000&auto=format&fit=crop&q=85',
  '소래습지생태공원': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '을왕리해수욕장 & 마시안 해변': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '을왕리해수욕장': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '청라 커널웨이 & 호수공원': 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1000&auto=format&fit=crop&q=85',
  '계양산': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&auto=format&fit=crop&q=85',
  '인스파이어 엔터테인먼트 리조트 (오로라 & 르 스페이스)': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1000&auto=format&fit=crop&q=85',
  '인스파이어 리조트': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1000&auto=format&fit=crop&q=85',
  '인스파이어 오로라': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1000&auto=format&fit=crop&q=85',
  '메이드림': '/spots/incheon-madelim-green-escape.jpg',
  '메이드림 (MADE林)': '/spots/incheon-madelim-green-escape.jpg',
  '겨울이 머무는 집 (홍대 사모예드 대형견 카페)': 'https://images.unsplash.com/photo-1529472119196-cb724127a98e?w=1000&auto=format&fit=crop&q=85',
  '고양이정원 (강서구 개화동 야외 정원 고양이 카페)': 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=1000&auto=format&fit=crop&q=85',
  '땡스네이쳐카페 (홍대 양 카페 & 와플 맛집)': 'https://images.unsplash.com/photo-1484557985045-edf25e08da73?w=1000&auto=format&fit=crop&q=85',
  '레인리포트 경리단길 (365일 비가 내리는 날씨 컨셉 카페)': 'https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?w=1000&auto=format&fit=crop&q=85',
  '그림카페 (연남동 2D 흑백 만화 속 착시 카페)': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '널담은공간 해방촌점 (1년 뒤 도착하는 실링왁스 미래편지 & 남산뷰 비건 루프탑)': 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1000&auto=format&fit=crop&q=85',
  '인천맥주 (동인천 개항로 수제맥주 브루어리 & 펍)': 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1000&auto=format&fit=crop&q=85',
  '일광전구 라이트하우스 (동인천 개항로 전구공장 카페)': 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=1000&auto=format&fit=crop&q=85',
  '바 다이어메이커 (송도 타임스페이스 LP바 & 칵테일 테라스)': 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1000&auto=format&fit=crop&q=85',
  '올드클락 (송도 아날로그 LP 뮤직바)': 'https://images.unsplash.com/photo-1461784121038-f088ca1e7714?w=1000&auto=format&fit=crop&q=85',
  '플레이그라운드 브루어리 탭룸 송도 (수제맥주 펍 & 다이닝)': 'https://images.unsplash.com/photo-1575037614876-c38a4d44f5b8?w=1000&auto=format&fit=crop&q=85',
  '바 세음 (부평 굴포천 청리단길 LP 카페 겸 위스키 바)': 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1000&auto=format&fit=crop&q=85',
  '온더바 (부평 평리단길 하몽 와인바 & 낮카페 밤펍)': 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1000&auto=format&fit=crop&q=85',
  '노체부어 (수원 행궁동 화성 성곽뷰 스페인 루프탑 펍 & 타파스 바)': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1000&auto=format&fit=crop&q=85',
  '정지영커피로스터즈 화홍문점/행궁본점 (수원 행리단길 성곽 루프탑 카페)': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '춘천 삼악산 호수케이블카 & 스카이워크 전망대 (국내 최장 3.6km)': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '원주 소금산 그랜드밸리 (출렁다리 · 소금잔도 · 스카이타워 · 울렁다리 · 케이블카)': 'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=1000&auto=format&fit=crop&q=85',
  '서해랑 제부도 해상케이블카 & 제부도 매바위 노을 (화성 전곡항)': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '갤러리아백화점 광교점 (OMA 렘 콜하스 건축 & 프리즘 유리 루프)': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1000&auto=format&fit=crop&q=85',
  '현대모터스튜디오 고양 (미래 모빌리티 체험관 & 키네틱 아트)': 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1000&auto=format&fit=crop&q=85',
  '광명동굴 (지하 미디어아트 빛의 공간 · 황금폭포 · 와인동굴)': 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1000&auto=format&fit=crop&q=85',
  '파주 마장호수 출렁다리 & 레드브릿지 루프탑 카페': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '파주출판도시 지혜의 숲 (8m 높이 대형 서가 & 노출콘크리트 건축)': 'https://images.unsplash.com/photo-1507842229356-51c61504d3ab?w=1000&auto=format&fit=crop&q=85',
  '시흥 웨이브파크 (거북섬 세계 최대급 인공서핑장)': 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=1000&auto=format&fit=crop&q=85',
  '더신더바 송도 (IGC 글로벌캠퍼스 & 연세대 교환학생 소셜 펍 나이트)': 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1000&auto=format&fit=crop&q=85',
  '이태원 더 크랙 하우스 – 매주 목요일 영어 펍퀴즈 & 피자 소셜 나이트': 'https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=1000&auto=format&fit=crop&q=85',
  '이태원 빌리언바 – 토요 영어 펍퀴즈 & 목요 한영 코미디 오픈마이크': 'https://images.unsplash.com/photo-1585699324551-f6c309eedeca?w=1000&auto=format&fit=crop&q=85',
  '스탠드업 서울 릿라운지 이태원 – 매주 일요일 영어 코미디 오픈마이크': 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=1000&auto=format&fit=crop&q=85',
  '서울 시티 임프로브 (SCI) – 주간 영어 즉흥 코미디 쇼 & 커뮤니티 오픈 잼': 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1000&auto=format&fit=crop&q=85',
  '클라임스퀘어 송도점 – 트리플스트리트 볼더링 & 20대 클라이밍 소셜 세션': 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1000&auto=format&fit=crop&q=85',

  
  // Extreme Sports & Adrenaline Activities (Seoul, Incheon, Gyeonggi)
  '가평 탑랜드 번지점프': '/spots/activity-gapyeong-topland-bungee.jpg',
  '플라이스테이션 코리아': '/spots/activity-yongin-flystation-skydiving.jpg',
  '롯데월드타워 스카이브릿지 투어': '/spots/activity-seoul-lotte-skybridge-tour.jpg',
  '양평 패러글라이딩 파크': '/spots/activity-yangpyeong-paragliding-park.jpg',
  '강화씨사이드리조트 루지': '/spots/activity-incheon-ganghwa-mega-luge.jpg',
  '강화씨사이드리조트 루지 & 회전전망대 곤돌라 (동양 최대급 1.8km 트랙)': '/spots/activity-incheon-ganghwa-mega-luge.jpg',
  '무의도 하나개해수욕장 씨스카이월드': '/spots/activity-incheon-muuido-sea-sky-zipline.jpg',
  '무의도 하나개해수욕장 씨스카이월드 짚라인 · 갯벌 사륜바이크(ATV) · 해상관광탐방로': '/spots/activity-incheon-muuido-sea-sky-zipline.jpg',
  '포천아트밸리 & 천주호 (화강암 채석장 에메랄드 호수 · 모노레일 · 천문과학관)': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '강화 교동도 대룡시장 & 강화평화전망대 (민통선 접경지역 · 망향대)': 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  '조양방직 (1933년 대한민국 최초 근대 방직공장 빈티지 뮤지엄 카페)': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '선재도 목섬 (CNN 선정 한국에서 가장 아름다운 섬 · 모세의 기적 바닷길)': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '시흥 배곧한울공원 해수체험장 & 오이도 빨간등대 (서해 노을 해안산책로)': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '강릉 하슬라아트월드 (동해 절벽 현대미술관 · 조각공원 · 피노키오박물관)': 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=1000&auto=format&fit=crop&q=85',
  '포천 한탄강 유네스코 세계지질공원 (한탄강 하늘다리 · 비둘기낭폭포 · 주상절리길)': 'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=1000&auto=format&fit=crop&q=85',
  "용인 대장금파크 (MBC 사극 오픈세트장 · BTS 슈가 '대취타' 촬영지)": 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  '스카이다이브 코리아 충주 드롭존': '/spots/activity-chungju-skydive-korea.jpg',
  // Social Events, Speed Dating & Community Mixers (Seoul & Incheon)
  '앱솔루트 서울 펍크롤 & 글로벌 파티 (홍대/이태원)': 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1000&auto=format&fit=crop&q=85',
  '와이엔에이(YNA) 홍대 글로벌 언어교환 & 애프터 펍 소셜': 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1000&auto=format&fit=crop&q=85',
  '다이얼로그 1:1 한영 언어교환 & 소셜 라운지 (해방촌 HBC)': 'https://images.unsplash.com/photo-1543269865-cbf427effbad?w=1000&auto=format&fit=crop&q=85',
  '서울셰어 20대 전용 인터내셔널 스피드데이팅 & 싱글즈 파티 (홍대)': 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1000&auto=format&fit=crop&q=85',
  '만남살롱 홍대점 (20대 로테이션 미팅 & 프라이빗 소개팅 라운지)': 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1000&auto=format&fit=crop&q=85',
  'GSM 홍대 테라스 – 글로벌 언어교환 & 애프터 펍 파티': 'https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=1000&auto=format&fit=crop&q=85',
  '슬로우 데이팅 서울 – 이태원 캔들라이트 와인 소셜 & 블라인드 미팅': 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1000&auto=format&fit=crop&q=85',
  '인왕산 한양도성 노을 트레킹 & 서촌 청년 치맥 소셜 모임': '/spots/seoul-inwangsan-trail.jpg',
  '홍대 레드버튼 글로벌 보드게임 & 소셜 네트워킹 나이트': 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=1000&auto=format&fit=crop&q=85',
  '인천 부평 20대 글로벌 언어교환 & 애프터 디너 소셜 모임': 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1000&auto=format&fit=crop&q=85',
  '송도 센트럴파크 편맥(편의점 맥주) & 글로벌 캠퍼스 소셜 모임': 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1000&auto=format&fit=crop&q=85',
  '인천 구월동 로데오 20대 싱글즈 스피드데이팅 & 1:1 로테이션 매칭 미팅': 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1000&auto=format&fit=crop&q=85',

  // Shopping & Pharmacies (Seoul & Incheon)
  '휴베이스 약국': 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=1000&auto=format&fit=crop&q=85',
  '휴베이스': 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=1000&auto=format&fit=crop&q=85',
  '휴베이스 서울역 메가약국': 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=1000&auto=format&fit=crop&q=85',
  '휴베이스 송도센트럴약국': 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=1000&auto=format&fit=crop&q=85',
  '보령약국': 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1000&auto=format&fit=crop&q=85',
  '온유약국': 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=1000&auto=format&fit=crop&q=85',
  '남시약국': 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1000&auto=format&fit=crop&q=85',
  '왕솔약국': 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1000&auto=format&fit=crop&q=85',
  '남대문 시장 약국거리': 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1000&auto=format&fit=crop&q=85',
  '인천국제공항 메가약국': 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=1000&auto=format&fit=crop&q=85',
  '인천국제공항 메가약국 (T1/T2)': 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=1000&auto=format&fit=crop&q=85',
  '더현대 서울': 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1000&auto=format&fit=crop&q=85',
  '더현대 서울 (The Hyundai Seoul)': 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1000&auto=format&fit=crop&q=85',
  '스타필드 코엑스몰': 'https://images.unsplash.com/photo-1568667256549-094345857637?w=1000&auto=format&fit=crop&q=85',
  '스타필드 코엑스몰 & 파르나스몰': 'https://images.unsplash.com/photo-1568667256549-094345857637?w=1000&auto=format&fit=crop&q=85',
  '롯데월드몰': 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=1000&auto=format&fit=crop&q=85',
  '롯데월드몰 잠실': 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=1000&auto=format&fit=crop&q=85',
  '타임스퀘어 영등포': 'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=1000&auto=format&fit=crop&q=85',
  '스퀘어원 인천': 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=1000&auto=format&fit=crop&q=85',
  '스퀘어원': 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=1000&auto=format&fit=crop&q=85',
  'AK플라자 홍대': 'https://images.unsplash.com/photo-1552346154-21d32810aba3?w=1000&auto=format&fit=crop&q=85',
  'AK플라자 홍대 & 홍대 브랜드거리': 'https://images.unsplash.com/photo-1552346154-21d32810aba3?w=1000&auto=format&fit=crop&q=85',
  '아이파크몰 용산': 'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=1000&auto=format&fit=crop&q=85',
  '아이파크몰 용산 (패션파크 4~6층)': 'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=1000&auto=format&fit=crop&q=85',
  '현대시티아울렛 동대문': 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=1000&auto=format&fit=crop&q=85',
  '신세계백화점 강남점': 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=1000&auto=format&fit=crop&q=85',
  '신세계백화점 강남점 & 센트럴시티': 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=1000&auto=format&fit=crop&q=85',
  '명동 눈스퀘어 & ABC마트 그랜드스테이지': 'https://images.unsplash.com/photo-1516478177764-9fe5bd7e9717?w=1000&auto=format&fit=crop&q=85',
  '눈스퀘어': 'https://images.unsplash.com/photo-1516478177764-9fe5bd7e9717?w=1000&auto=format&fit=crop&q=85',
  'ABC마트 그랜드스테이지 명동': 'https://images.unsplash.com/photo-1516478177764-9fe5bd7e9717?w=1000&auto=format&fit=crop&q=85',

  // Seoul - Palaces & Icons
  '경복궁': 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  '북촌한옥마을': 'https://images.unsplash.com/photo-1578637387939-43c525550085?w=1000&auto=format&fit=crop&q=85',
  '창덕궁과 후원': 'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?w=1000&auto=format&fit=crop&q=85',
  '창덕궁': 'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?w=1000&auto=format&fit=crop&q=85',
  '덕수궁 & 돌담길': 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1000&auto=format&fit=crop&q=85',
  '덕수궁': 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1000&auto=format&fit=crop&q=85',
  'N서울타워 / 남산공원': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=1000&auto=format&fit=crop&q=85',
  'N서울타워': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=1000&auto=format&fit=crop&q=85',
  '남산타워': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=1000&auto=format&fit=crop&q=85',
  '롯데월드타워 서울스카이': 'https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?w=1000&auto=format&fit=crop&q=85',
  '롯데월드타워': 'https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?w=1000&auto=format&fit=crop&q=85',
  '롯데월드타워 SKY31 라운지': 'https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1000&auto=format&fit=crop&q=85',
  '63스퀘어 / 63아트': 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1000&auto=format&fit=crop&q=85',
  '63빌딩': 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1000&auto=format&fit=crop&q=85',
  '정동전망대': 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  '대한민국역사박물관 8층 옥상정원': 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  '반포대교 달빛무지개분수 & 세빛섬': 'https://images.unsplash.com/photo-1617653202545-931490e8d7e7?w=1000&auto=format&fit=crop&q=85',
  '반포대교': 'https://images.unsplash.com/photo-1617653202545-931490e8d7e7?w=1000&auto=format&fit=crop&q=85',
  '별마당 도서관': 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  '동대문디자인플라자 (DDP)': 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1000&auto=format&fit=crop&q=85',
  '동대문디자인플라자': 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1000&auto=format&fit=crop&q=85',
  '익선동 한옥거리': 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1000&auto=format&fit=crop&q=85',
  '익선동': 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1000&auto=format&fit=crop&q=85',
  '광장시장': 'https://images.unsplash.com/photo-1568084680786-a84f91d1153c?w=1000&auto=format&fit=crop&q=85',
  '여의도 한강공원': 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1000&auto=format&fit=crop&q=85',
  '홍대 걷고싶은거리': 'https://images.unsplash.com/photo-1563245372-f21724e3856d?w=1000&auto=format&fit=crop&q=85',
  '청계천': 'https://images.unsplash.com/photo-1546874177-9e664107314e?w=1000&auto=format&fit=crop&q=85',
  '은평한옥마을': 'https://images.unsplash.com/photo-1578637387939-43c525550085?w=1000&auto=format&fit=crop&q=85',
  '성수동 카페거리': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '성수동': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '디올 성수 & 대림창고': 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1000&auto=format&fit=crop&q=85',
  '디올 성수': 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1000&auto=format&fit=crop&q=85',
  '서울숲': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '인왕산 한양도성': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&auto=format&fit=crop&q=85',
  '청와대': 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  '봉은사': 'https://images.unsplash.com/photo-1596401057633-54a8fe8ef647?w=1000&auto=format&fit=crop&q=85',
  '연남동 경의선숲길': 'https://images.unsplash.com/photo-1563245372-f21724e3856d?w=1000&auto=format&fit=crop&q=85',
  '한남동 카페거리 & 리움미술관': 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  '한남동': 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  '낙산공원 & 한양도성': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=1000&auto=format&fit=crop&q=85',
  '낙산공원': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=1000&auto=format&fit=crop&q=85',
  '하늘공원': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '용리단길': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '망원시장 & 망리단길': 'https://images.unsplash.com/photo-1568084680786-a84f91d1153c?w=1000&auto=format&fit=crop&q=85',
  '하이커 그라운드': 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  '윤슬 (만리동광장)': 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1000&auto=format&fit=crop&q=85',
  '윤슬': 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1000&auto=format&fit=crop&q=85',
  '홍제유연 (유진상가 지하)': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  '홍제유연': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  'SeMA 벙커 (여의도 지하벙커)': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1000&auto=format&fit=crop&q=85',
  'SeMA 벙커': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1000&auto=format&fit=crop&q=85',
  '여의도 지하벙커': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1000&auto=format&fit=crop&q=85',

  // Limited Light Festivals & Night Shows
  '경복궁 야간관람': 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  '서울세계불꽃축제 (여의도 한강공원)': 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1000&auto=format&fit=crop&q=85',
  '서울세계불꽃축제': 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1000&auto=format&fit=crop&q=85',
  '고양호수예술축제 불꽃 드론쇼': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  '고양호수예술축제': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  '수원화성 미디어아트': 'https://images.unsplash.com/photo-1528728329032-2972f65dfb3f?w=1000&auto=format&fit=crop&q=85',
  '한강 드론 라이트쇼 (뚝섬한강공원)': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  '한강 드론 라이트쇼': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  '서울달 (여의도 보름달 계류식 가스기구)': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  '서울달': 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1000&auto=format&fit=crop&q=85',
  '노들섬 & 달빛노들': 'https://images.unsplash.com/photo-1532274402911-5a369e4c4bb5?w=1000&auto=format&fit=crop&q=85',
  '노들섬': 'https://images.unsplash.com/photo-1532274402911-5a369e4c4bb5?w=1000&auto=format&fit=crop&q=85',
  '노들섬 (Nodeul Island)': 'https://images.unsplash.com/photo-1532274402911-5a369e4c4bb5?w=1000&auto=format&fit=crop&q=85',
  '열린송현녹지광장': 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=1000&auto=format&fit=crop&q=85',
  '서울야외도서관 (책읽는 서울광장 & 광화문 책마당)': 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=1000&auto=format&fit=crop&q=85',
  '서울야외도서관': 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=1000&auto=format&fit=crop&q=85',

  // Gyeonggi
  '수원화성 / 플라잉수원': 'https://images.unsplash.com/photo-1528728329032-2972f65dfb3f?w=1000&auto=format&fit=crop&q=85',
  '수원화성': 'https://images.unsplash.com/photo-1528728329032-2972f65dfb3f?w=1000&auto=format&fit=crop&q=85',
  '스타필드 수원 별마당도서관': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1000&auto=format&fit=crop&q=85',
  '스타필드 수원': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1000&auto=format&fit=crop&q=85',

  // Busan
  '감천문화마을': 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1000&auto=format&fit=crop&q=85',
  '해동용궁사': 'https://images.unsplash.com/photo-1596401057633-54a8fe8ef647?w=1000&auto=format&fit=crop&q=85',
  '해운대 블루라인파크 (스카이캡슐)': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '해운대블루라인파크': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '광안리해수욕장 / 광안대교': 'https://images.unsplash.com/photo-1617653202545-931490e8d7e7?w=1000&auto=format&fit=crop&q=85',
  '광안리해수욕장': 'https://images.unsplash.com/photo-1617653202545-931490e8d7e7?w=1000&auto=format&fit=crop&q=85',
  '흰여울문화마을': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '아르떼뮤지엄 부산': 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=1000&auto=format&fit=crop&q=85',
  '아르떼뮤지엄': 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=1000&auto=format&fit=crop&q=85',
  '부산 엑스더스카이 (LCT 랜드마크타워)': 'https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?w=1000&auto=format&fit=crop&q=85',
  '부산 엑스더스카이': 'https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?w=1000&auto=format&fit=crop&q=85',
  '밀락더마켓': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',

  // Jeju
  '성산일출봉': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&auto=format&fit=crop&q=85',
  '협재해수욕장': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '섭지코지': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&auto=format&fit=crop&q=85',
  '산굼부리': 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=1000&auto=format&fit=crop&q=85',
  '다랑쉬오름': 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1000&auto=format&fit=crop&q=85',
  '다랑쉬오름 (월랑봉)': 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1000&auto=format&fit=crop&q=85',
  '용눈이오름': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  '제주마방목지': 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=1000&auto=format&fit=crop&q=85',
  '제주4·3평화공원': 'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=1000&auto=format&fit=crop&q=85',
  '제주4.3평화공원': 'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=1000&auto=format&fit=crop&q=85',
  '유민아르누보뮤지엄': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1000&auto=format&fit=crop&q=85',
  '소농로드': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '움튼 델리카테슨 구좌세화점': 'https://images.unsplash.com/photo-1509722747041-616f39b57569?w=1000&auto=format&fit=crop&q=85',
  '움튼 델리카테슨': 'https://images.unsplash.com/photo-1509722747041-616f39b57569?w=1000&auto=format&fit=crop&q=85',
  '바투모루': 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  '이런날엔': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1000&auto=format&fit=crop&q=85',
  '비수기애호가': 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1000&auto=format&fit=crop&q=85',
  '쉬어갓': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  '올데이모즈 조천녹차밭점': 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=1000&auto=format&fit=crop&q=85',
  '올데이모즈': 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=1000&auto=format&fit=crop&q=85',
  '봉덕': 'https://images.unsplash.com/photo-1590301157890-4810ed352733?w=1000&auto=format&fit=crop&q=85',
  '베케': 'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=1000&auto=format&fit=crop&q=85',
  '디어브리즈': 'https://images.unsplash.com/photo-1513519245088-0e12902e5a38?w=1000&auto=format&fit=crop&q=85',

  // Jeju - Vegetarian & Vegan Culinary Highlights
  '야뜨리': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '야뜨리 인도요리': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '베이커리 보엠': 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=1000&auto=format&fit=crop&q=85',
  '빵귿': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',
  '루루비건': 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=1000&auto=format&fit=crop&q=85',
  '그날의 조각': 'https://images.unsplash.com/photo-1587314168485-3236d6710814?w=1000&auto=format&fit=crop&q=85',


  '인디언키친 애월본점': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '인디언키친': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '톰톰카레': 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=1000&auto=format&fit=crop&q=85',
  '아살람': 'https://images.unsplash.com/photo-1541518763669-27fef04b14ea?w=1000&auto=format&fit=crop&q=85',
  '와르다': 'https://images.unsplash.com/photo-1594041680534-e8c8cdebd659?w=1000&auto=format&fit=crop&q=85',
  '와르다 레스토랑': 'https://images.unsplash.com/photo-1594041680534-e8c8cdebd659?w=1000&auto=format&fit=crop&q=85',
  '칠분의오': 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=1000&auto=format&fit=crop&q=85',
  '비건테이블 바람': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '도토리키친 본점': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '도토리키친': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '담아래 본점': 'https://images.unsplash.com/photo-1553163147-622ab57be1c7?w=1000&auto=format&fit=crop&q=85',
  '담아래': 'https://images.unsplash.com/photo-1553163147-622ab57be1c7?w=1000&auto=format&fit=crop&q=85',
  '감미롭다': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1000&auto=format&fit=crop&q=85',
  '러빙헛 남원점': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '펜고호다': 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=1000&auto=format&fit=crop&q=85',
  '두리둠비': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '두리둠비 제주중문본점': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '카고크루즈': 'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=1000&auto=format&fit=crop&q=85',
  '소락': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '숨비다': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '오른': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',
  '크랩스토리': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '오름해산물': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '제주 중앙 이슬람 성원': 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=1000&auto=format&fit=crop&q=85',

  '다소니': '/spots/jeju-dasoni.jpg',
  '다손': '/spots/jeju-dasoni.jpg',
  '앤드유카페': '/spots/jeju-and-you-cafe.jpg',
  '앤드유': '/spots/jeju-and-you-cafe.jpg',
  '러빙헛 제주': '/spots/jeju-loving-hut.jpg',
  '러빙헛': '/spots/jeju-loving-hut.jpg',
  '카페 901': '/spots/jeju-cafe-901.jpg',
  '카페901': '/spots/jeju-cafe-901.jpg',
  '연우네': '/spots/jeju-yeonwoone.jpg',
  '작은부엌': '/spots/jeju-small-kitchen.jpg',
  '바그다드': '/spots/jeju-bagdad-indian.jpg',
  '바그다드 제주': '/spots/jeju-bagdad-indian.jpg',
  '라즈마할': '/spots/jeju-rajmahal-indian.jpg',
  '라즈마할 인도음식점': '/spots/jeju-rajmahal-indian.jpg',
  '신선원': '/spots/jeju-sinsunwon-temple.jpg',
  '초록바람': '/spots/jeju-green-wave.jpg',
  '개인의 취향': '/spots/jeju-taste-of-jeju.jpg',
  '포케올데이 제주': '/spots/jeju-poke-all-day.jpg',
  '포케올데이 제주이도점': '/spots/jeju-poke-all-day.jpg',

  // Jeju - Nature, Landmarks & Architecture
  '한라산 국립공원': '/spots/jeju-hallasan-national-park.jpg',
  '한라산': '/spots/jeju-hallasan-national-park.jpg',
  '영실코스': '/spots/jeju-hallasan-national-park.jpg',
  '함덕해수욕장': '/spots/jeju-hamdeok-beach.jpg',
  '함덕해수욕장 & 서우봉': '/spots/jeju-hamdeok-beach.jpg',
  '만장굴': '/spots/jeju-manjanggul-cave.jpg',
  '대포주상절리대': '/spots/jeju-jusangjeolli-cliff.jpg',
  '주상절리대': '/spots/jeju-jusangjeolli-cliff.jpg',
  '정방폭포': '/spots/jeju-jeongbang-waterfall.jpg',
  '비자림': '/spots/jeju-bijarim-forest.jpg',
  '오설록 티뮤지엄': '/spots/jeju-osulloc-tea-museum.jpg',
  '오설록': '/spots/jeju-osulloc-tea-museum.jpg',
  '약천사': '/spots/jeju-yakcheonsa-temple.jpg',
  '카멜리아힐': '/spots/jeju-camellia-hill.jpg',
  '애월 한담해변 산책로': '/spots/jeju-aewol-handam-walk.jpg',
  '한담해변': '/spots/jeju-aewol-handam-walk.jpg',
  '애월 카페거리': '/spots/jeju-aewol-handam-walk.jpg',
  '아르떼뮤지엄 제주': '/spots/jeju-arte-museum.jpg',
  '본태박물관': '/spots/jeju-bonte-museum.jpg',

  // Gyeongju & Jeonju & Gangwon
  '동궁과 월지 (안압지)': 'https://images.unsplash.com/photo-1546874177-9e664107314e?w=1000&auto=format&fit=crop&q=85',
  '동궁과 월지': 'https://images.unsplash.com/photo-1546874177-9e664107314e?w=1000&auto=format&fit=crop&q=85',
  '불국사': 'https://images.unsplash.com/photo-1596401057633-54a8fe8ef647?w=1000&auto=format&fit=crop&q=85',
  '전주한옥마을': 'https://images.unsplash.com/photo-1578637387939-43c525550085?w=1000&auto=format&fit=crop&q=85',
  '남이섬': 'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=1000&auto=format&fit=crop&q=85',
  '뮤지엄 산 (원주 오크밸리)': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1000&auto=format&fit=crop&q=85',
  '뮤지엄 산': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1000&auto=format&fit=crop&q=85',

  // Inha University & Incheon Food Hotspots
  '커리야 인하대점': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '커리야': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '써브웨이 인하대점': 'https://images.unsplash.com/photo-1509722747041-616f39b57569?w=1000&auto=format&fit=crop&q=85',
  '써브웨이': 'https://images.unsplash.com/photo-1509722747041-616f39b57569?w=1000&auto=format&fit=crop&q=85',
  '에그드랍 인하대후문점': 'https://images.unsplash.com/photo-1525351484163-7529414344d8?w=1000&auto=format&fit=crop&q=85',
  '에그드랍': 'https://images.unsplash.com/photo-1525351484163-7529414344d8?w=1000&auto=format&fit=crop&q=85',
  '이삭토스트 인하대점': 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=1000&auto=format&fit=crop&q=85',
  '이삭토스트': 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=1000&auto=format&fit=crop&q=85',
  '아미고스': 'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=1000&auto=format&fit=crop&q=85',
  '미식당 (인하로67번길 6)': 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1000&auto=format&fit=crop&q=85',
  '미식당': 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1000&auto=format&fit=crop&q=85',
  '투파인드피터 (인하대점)': 'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=1000&auto=format&fit=crop&q=85',
  '투파인드피터': 'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=1000&auto=format&fit=crop&q=85',
  '롤링파스타 (인하대점)': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1000&auto=format&fit=crop&q=85',
  '롤링파스타': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1000&auto=format&fit=crop&q=85',
  '키친 라르고': 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1000&auto=format&fit=crop&q=85',
  '키친라르고': 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1000&auto=format&fit=crop&q=85',
  '코피아': 'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=1000&auto=format&fit=crop&q=85',
  '다슴': 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=1000&auto=format&fit=crop&q=85',
  '포케올데이 인하대역점': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '포케올데이': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '아라베스크': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '마하라자 송도': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1000&auto=format&fit=crop&q=85',
  '마하라자': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1000&auto=format&fit=crop&q=85',
  '비하니': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '지구본': 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=1000&auto=format&fit=crop&q=85',
  '더 비기닝': 'https://images.unsplash.com/photo-1525351484163-7529414344d8?w=1000&auto=format&fit=crop&q=85',
  '그린바스켓 송도': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '산채비빔밥 / 순천돌솥밥': 'https://images.unsplash.com/photo-1590301157890-4810ed352733?w=1000&auto=format&fit=crop&q=85',

  // Seoul Halal & Universal Spots
  '이드 할랄': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '마칸': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '할랄가이즈': 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=1000&auto=format&fit=crop&q=85',
  '양굿': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '할랄키친': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '케르반': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',
  '부산집 명동': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '타지 명동점': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '타지 명동': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '구르카 인도네팔레스토랑 명동': 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=1000&auto=format&fit=crop&q=85',
  '깜풍쿠 명동': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',
  '포탈라 종로점': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '포탈라': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '페트라': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '모로코코 카페': 'https://images.unsplash.com/photo-1541518763669-27fef04b14ea?w=1000&auto=format&fit=crop&q=85',
  '죠티인도레스토랑 신촌': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1000&auto=format&fit=crop&q=85',
  '예티 홍대': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '허머스키친 코엑스 파르나스몰점': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '깔리 대학로점': 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=1000&auto=format&fit=crop&q=85',
  '에베레스트 본점 동대문': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '머노까머나 대학로점': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '에베레스트 커리월드': 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=1000&auto=format&fit=crop&q=85',
  '뿌자 동대문점': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1000&auto=format&fit=crop&q=85',
  '페르시안궁전': 'https://images.unsplash.com/photo-1541518763669-27fef04b14ea?w=1000&auto=format&fit=crop&q=85',
  '헬로인디아 알와하 부산해운대': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '나마스테 해운대점': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '볼리우드 광안리': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1000&auto=format&fit=crop&q=85',
  '봄베이브로이 남포점': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '와룽 자야 음박 티아 부산남포': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',
  '아살람 레스토랑 제주': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '우리봉식당': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '제주뚝배기': 'https://images.unsplash.com/photo-1563245372-f21724e3856d?w=1000&auto=format&fit=crop&q=85',
  '코코마마 성산점': 'https://images.unsplash.com/photo-1559847844-5315695dadae?w=1000&auto=format&fit=crop&q=85',
  '제주소녀 (비자숲길)': 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=1000&auto=format&fit=crop&q=85',
  '밥짓는시간 (구좌 평대리)': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1000&auto=format&fit=crop&q=85',
  '칠분의오 (구좌읍)': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '야뜨리 인도요리 (서귀포)': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',
  '감미롭다제주 (서귀포 호근동)': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '제주베지스 (한경면)': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&auto=format&fit=crop&q=85',
  '인디언키친 (애월 본점)': 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=1000&auto=format&fit=crop&q=85',
  '비건테이블바람 (애월읍)': 'https://images.unsplash.com/photo-1547592180-85f173990554?w=1000&auto=format&fit=crop&q=85',
  '자카르타 제주 (동문시장)': 'https://images.unsplash.com/photo-1562967914-608f82629710?w=1000&auto=format&fit=crop&q=85',
  '비원 삼계탕 (노형동 / 한라수목원)': 'https://images.unsplash.com/photo-1547592180-85f173990554?w=1000&auto=format&fit=crop&q=85',
  '바다잔치 (탑동)': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '바다풍경 (용담해안도로)': 'https://images.unsplash.com/photo-1534422298391-e4f8c172dddb?w=1000&auto=format&fit=crop&q=85',
  '스와갓 송도점': 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1000&auto=format&fit=crop&q=85',

  // Seoul Vegetarian & Vegan Spots
  '코엑스 아쿠아리움': 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1000&auto=format&fit=crop&q=85',
  '강남 실탄사격장': 'https://images.unsplash.com/photo-1595590424283-b8f17842773f?w=1000&auto=format&fit=crop&q=85',
  '르챔버': 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1000&auto=format&fit=crop&q=85',
  '세빛섬 튜브스터': '/spots/seoul-banpo-rainbow-bridge.jpg',
  '포탈 PC 강남점': 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1000&auto=format&fit=crop&q=85',

  '스매싱볼 강남점': 'https://images.unsplash.com/photo-1538388149542-5e24932d11a8?w=1000&auto=format&fit=crop&q=85',
  '제로월드 강남점': 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1000&auto=format&fit=crop&q=85',
  '압구정로데오 & 도산공원': 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1000&auto=format&fit=crop&q=85',


  '서울로봇인공지능미술관 (RAIM)': 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1000&auto=format&fit=crop&q=85',
  '마포 문화비축기지': 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Mapo_Park_-_Human_Nature.jpg/1280px-Mapo_Park_-_Human_Nature.jpg',
  '제네시스 수지': 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1000&auto=format&fit=crop&q=85',
  '송도 트라이볼': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1000&auto=format&fit=crop&q=85',

  '[대학생 전용] 홍대 KULL 영어-한국어 언어교환 & 애프터파티': 'https://globalseoulmates-images.s3.ap-northeast-2.amazonaws.com/image-b3rpnsg7s3g.jpeg',
  '🇩🇪 GSM 강남 독일어-한국어 언어교환 모임': 'https://globalseoulmates-images.s3.ap-northeast-2.amazonaws.com/image-2n4ur4ebbws.jpeg',

  '발우공양': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '오세계향': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  '플랜트 이태원': 'https://images.unsplash.com/photo-1520072959219-c595dc870360?w=1000&auto=format&fit=crop&q=85',
  '플랜트': 'https://images.unsplash.com/photo-1520072959219-c595dc870360?w=1000&auto=format&fit=crop&q=85',
  '몽크스부처 한남': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '몽크스부처': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1000&auto=format&fit=crop&q=85',
  '알타': 'https://images.unsplash.com/photo-1525755662778-989d0524087e?w=1000&auto=format&fit=crop&q=85',
  '마루자연식김밥': 'https://images.unsplash.com/photo-1553163147-622ab57be1c7?w=1000&auto=format&fit=crop&q=85',
  '쿡앤북': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',
  '드렁큰비건': 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1000&auto=format&fit=crop&q=85',
  '포레스트 키친': 'https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=1000&auto=format&fit=crop&q=85',
  '바이두부': 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=1000&auto=format&fit=crop&q=85',
  '마지': 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',

  // Activities & Fun (Minigolf, Screen Golf, Sports Monster, Archery, etc.)
  '크레이지슈트 미니골프': 'https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=1000&auto=format&fit=crop&q=85',
  '크레이지슈트 미니골프 홍대': 'https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=1000&auto=format&fit=crop&q=85',
  '골프존파크': 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1000&auto=format&fit=crop&q=85',
  '골프존파크 강남 테헤란로점': 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1000&auto=format&fit=crop&q=85',
  '스몹 바이 스포츠몬스터': '/spots/fitness-indoor-smob-sports-monster.jpg',
  '스몹 바이 스포츠몬스터 스타필드 고양': '/spots/fitness-indoor-smob-sports-monster.jpg',
  '로빈훗 실내 양궁카페': 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1000&auto=format&fit=crop&q=85',
  '로빈훗 실내 양궁카페 홍대점': 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1000&auto=format&fit=crop&q=85',
  '다이내믹 메이즈 인사동점': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1000&auto=format&fit=crop&q=85',
  '다이내믹 메이즈': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1000&auto=format&fit=crop&q=85',
  '키에스케이프 홍대점': 'https://images.unsplash.com/photo-1514306191717-452ec28c7814?w=1000&auto=format&fit=crop&q=85',
  '키에스케이프': 'https://images.unsplash.com/photo-1514306191717-452ec28c7814?w=1000&auto=format&fit=crop&q=85',
  '더클라임 클라이밍 홍대점': 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1000&auto=format&fit=crop&q=85',
  '더클라임 클라이밍 짐앤샵 연남점': 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1000&auto=format&fit=crop&q=85',
  '더클라임 연남점': 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1000&auto=format&fit=crop&q=85',
  '더클라임': 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1000&auto=format&fit=crop&q=85',
  '뚝섬 한강공원 카약 & 패들보드': 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1000&auto=format&fit=crop&q=85',
  '롯데월드 아이스링크': 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1000&auto=format&fit=crop&q=85',
  '롯데월드 아이스링크 잠실': 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1000&auto=format&fit=crop&q=85',
  '롤러시티': 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=1000&auto=format&fit=crop&q=85',
  '롤러시티 레트로 롤러스케이트장': 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=1000&auto=format&fit=crop&q=85',
  '9.81 파크 제주': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1000&auto=format&fit=crop&q=85',
  '9.81 파크 제주 무동력 레이싱': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1000&auto=format&fit=crop&q=85',
  '9.81 파크': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1000&auto=format&fit=crop&q=85',
  '부산 다이아몬드베이 요트투어': 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1000&auto=format&fit=crop&q=85',
  '다이아몬드베이 요트투어': 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1000&auto=format&fit=crop&q=85',
};

export function getCuratedAuthenticPhoto(nameKr?: string, name?: string): string | null {
  if (nameKr && CURATED_AUTHENTIC_PHOTOS[nameKr]) {
    return CURATED_AUTHENTIC_PHOTOS[nameKr];
  }
  for (const [key, url] of Object.entries(CURATED_AUTHENTIC_PHOTOS)) {
    if (nameKr && (nameKr.includes(key) || key.includes(nameKr))) {
      return url;
    }
    if (name && (name.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(name.toLowerCase()))) {
      return url;
    }
  }
  return null;
}

export interface SpotSocialLinks {
  hashtag: string;
  instagramUrl: string;
  tiktokUrl: string;
  naverBlogUrl: string;
}

export interface CuratedSocialConfig {
  tag: string;
  displayHashtag: string;
  instagramUrl?: string;
  tiktokQuery?: string;
}

/**
 * Curated, verified, viral Instagram & TikTok tags for Korean spots.
 * Solves the issue where long English concatenated names returned 0 results on Instagram.
 */
export const CURATED_SPOT_SOCIALS: Record<string, CuratedSocialConfig> = {
  // ==========================================
  // ACTIVITIES & ACTION
  // ==========================================
  'activity-seoul-crazychute-minigolf': {
    tag: '미니골프',
    displayHashtag: '#미니골프',
    tiktokQuery: 'Crazy Chute mini golf Hongdae Seoul',
  },
  'activity-seoul-golfzon-screen-golf': {
    tag: '골프존',
    displayHashtag: '#골프존',
    instagramUrl: 'https://www.instagram.com/explore/tags/%EA%B3%A8%ED%94%84%EC%A1%B4/',
    tiktokQuery: 'Golfzon screen golf Korea',
  },
  'activity-goyang-smob-sports-monster': {
    tag: '스몹',
    displayHashtag: '#스몹',
    instagramUrl: 'https://www.instagram.com/explore/tags/%EC%8A%A4%EB%AA%B9/',
    tiktokQuery: 'Smob Sports Monster Starfield Korea',
  },
  'activity-seoul-robinhood-archery': {
    tag: '양궁카페',
    displayHashtag: '#양궁카페',
    instagramUrl: 'https://www.instagram.com/explore/tags/%EC%96%91%EA%B6%81%EC%B9%B4%ED%8E%98/',
    tiktokQuery: 'Indoor Archery Cafe Seoul Hongdae',
  },
  'activity-seoul-dynamic-maze': {
    tag: '다이나믹메이즈',
    displayHashtag: '#다이나믹메이즈',
    instagramUrl: 'https://www.instagram.com/explore/tags/%EB%8B%A4%EC%9D%B4%EB%82%98%EB%AF%B9%EB%A9%94%EC%9D%B4%EC%A6%88/',
    tiktokQuery: 'Dynamic Maze Insadong Seoul',
  },
  'activity-seoul-key-escape-hongdae': {
    tag: '키이스케이프',
    displayHashtag: '#키이스케이프',
    instagramUrl: 'https://www.instagram.com/explore/tags/%ED%82%A4%EC%9D%B4%EC%8A%A4%EC%BC%80%EC%9D%B4%ED%94%84/',
    tiktokQuery: 'Key Escape room Hongdae Seoul',
  },
  'activity-seoul-the-climb-hongdae': {
    tag: '더클라임',
    displayHashtag: '#더클라임',
    instagramUrl: 'https://www.instagram.com/theclimb_yeonnam/',
    tiktokQuery: 'The Climb bouldering gym Yeonnam Hongdae Seoul',
  },
  'activity-seoul-ttukseom-hangang-kayak': {
    tag: '한강패들보드',
    displayHashtag: '#한강패들보드',
    instagramUrl: 'https://www.instagram.com/explore/tags/%ED%95%9C%EA%B0%95%ED%8C%A8%EB%93%A4%EB%B3%B4%EB%93%9C/',
    tiktokQuery: 'Ttukseom Hangang sunset kayak paddleboard Seoul',
  },
  'activity-seoul-lotte-world-ice-garden': {
    tag: '롯데월드아이스링크',
    displayHashtag: '#롯데월드아이스링크',
    instagramUrl: 'https://www.instagram.com/explore/tags/%EB%A1%AF%EB%8D%B0%EC%9B%94%EB%93%9C%EC%95%84%EC%9D%B4%EC%8A%A4%EB%A7%81%ED%81%AC/',
    tiktokQuery: 'Lotte World Ice Garden rink Jamsil Seoul',
  },
  'activity-seoul-roller-city': {
    tag: '롤러시티',
    displayHashtag: '#롤러시티',
    instagramUrl: 'https://www.instagram.com/explore/tags/%EB%A1%A4%EB%9F%AC%EC%8B%9C%ED%8B%B0/',
    tiktokQuery: 'Roller City roller skating rink Seoul',
  },
  'activity-jeju-981-park': {
    tag: '981파크',
    displayHashtag: '#981파크',
    instagramUrl: 'https://www.instagram.com/explore/tags/981%ED%8C%8C%ED%81%AC/',
    tiktokQuery: '9.81 Park Jeju gravity racing kart',
  },
  'activity-busan-diamond-bay-yacht': {
    tag: '부산요트투어',
    displayHashtag: '#부산요트투어',
    instagramUrl: 'https://www.instagram.com/explore/tags/%EB%B6%80%EC%82%B0%EC%9A%94%ED%8A%B8%ED%88%AC%EC%96%B4/',
    tiktokQuery: 'Diamond Bay Busan yacht tour Gwangan bridge',
  },

  // ==========================================
  // INCHEON HIGHLIGHTS
  // ==========================================
  'incheon-songdo-central-park': {
    tag: '송도센트럴파크',
    displayHashtag: '#송도센트럴파크',
    tiktokQuery: 'Songdo Central Park Incheon Moon boat',
  },
  'incheon-inspire-resort-aurora': {
    tag: '인스파이어',
    displayHashtag: '#인스파이어',
    tiktokQuery: 'Inspire Resort Aurora whale show Incheon',
  },
  'event-incheon-sky-festival': {
    tag: '스카이페스티벌',
    displayHashtag: '#인천공항스카이페스티벌',
    tiktokQuery: 'Incheon Airport Sky Festival Inspire Arena',
  },
  'event-nowon-moonlight-walk': {
    tag: '노원달빛산책',
    displayHashtag: '#노원달빛산책',
    tiktokQuery: 'Nowon Moonlight Walk Seoul light art Danghyeoncheon',
  },
  'event-incheon-round-festival': {
    tag: '라운드페스티벌',
    displayHashtag: '#ROUNDinKorea',
    tiktokQuery: 'ROUND in Korea 2026 Incheon Namdong Gymnasium',
  },
  'event-hwadam-forest-autumn': {
    tag: '화담숲',
    displayHashtag: '#화담숲단풍',
    tiktokQuery: 'Hwadam Forest autumn foliage monorail Korea',
  },
  'event-yangpyeong-yongmunsan-ginkgo': {
    tag: '용문사은행나무',
    displayHashtag: '#용문사은행나무',
    tiktokQuery: 'Yongmunsan 1100 year ginkgo tree Yangpyeong',
  },
  'event-jogyesa-chrysanthemum-festival': {
    tag: '조계사국화축제',
    displayHashtag: '#조계사국화축제',
    tiktokQuery: 'Jogyesa temple chrysanthemum festival Seoul',
  },

  'incheon-wolmido-theme-park': {
    tag: '월미도',
    displayHashtag: '#월미도',
    tiktokQuery: 'Wolmido island theme park Incheon Disco Pang Pang',
  },
  'incheon-songdo-triple-street': {
    tag: '트리플스트리트',
    displayHashtag: '#트리플스트리트',
    tiktokQuery: 'Songdo Triple Street Incheon umbrellas',
  },
  'incheon-chinatown': {
    tag: '인천차이나타운',
    displayHashtag: '#인천차이나타운',
    tiktokQuery: 'Incheon Chinatown Korea',
  },
  'incheon-cheongna-canal-way': {
    tag: '청라호수공원',
    displayHashtag: '#청라호수공원',
    tiktokQuery: 'Cheongna Lake Park Canal Incheon',
  },
  'incheon-gyeyangsan-mountain': {
    tag: '계양산',
    displayHashtag: '#계양산',
    tiktokQuery: 'Gyeyangsan mountain Incheon night view',
  },
  'incheon-cosmo-40': {
    tag: '코스모40',
    displayHashtag: '#코스모40',
    tiktokQuery: 'Cosmo 40 Incheon cafe arts center',
  },
  'incheon-sorae-ecology-park': {
    tag: '소래습지생태공원',
    displayHashtag: '#소래습지생태공원',
    tiktokQuery: 'Sorae ecology park windmills Incheon',
  },
  'incheon-eurwangni-masian-beach': {
    tag: '을왕리해수욕장',
    displayHashtag: '#을왕리해수욕장',
    tiktokQuery: 'Eurwangni beach Masian sunset bakery Incheon',
  },
  'incheon-madelim-green-escape': {
    tag: '메이드림',
    displayHashtag: '#메이드림',
    instagramUrl: 'https://www.instagram.com/madelim_official/',
    tiktokQuery: 'Made Lim cafe Incheon Yeongjongdo',
  },

  // ==========================================
  // TOP SEOUL & OTHER ICONS
  // ==========================================
  'seoul-starfield-library': {
    tag: '별마당도서관',
    displayHashtag: '#별마당도서관',
    tiktokQuery: 'Starfield Library COEX Mall Seoul',
  },
  'suwon-starfield-library': {
    tag: '별마당도서관',
    displayHashtag: '#별마당도서관',
    tiktokQuery: 'Starfield Suwon Byeolmadang Library',
  },
  'seoul-gyeongbokgung': {
    tag: '경복궁',
    displayHashtag: '#경복궁',
    tiktokQuery: 'Gyeongbokgung Palace Seoul hanbok',
  },
  'seoul-bukchon-hanok': {
    tag: '북촌한옥마을',
    displayHashtag: '#북촌한옥마을',
    tiktokQuery: 'Bukchon Hanok Village Seoul',
  },
  'seoul-n-seoul-tower': {
    tag: 'N서울타워',
    displayHashtag: '#N서울타워',
    tiktokQuery: 'N Seoul Tower Namsan view',
  },
  'seoul-lotte-world-tower': {
    tag: '롯데월드타워',
    displayHashtag: '#롯데월드타워',
    tiktokQuery: 'Lotte World Tower Seoul Sky',
  },
  'seoul-ddp-dongdaemun': {
    tag: '동대문디자인플라자',
    displayHashtag: '#DDP',
    tiktokQuery: 'DDP Dongdaemun Design Plaza Seoul night view',
  },
  'seoul-naksan-park-fortress': {
    tag: '낙산공원',
    displayHashtag: '#낙산공원',
    tiktokQuery: 'Naksan Park Seoul Fortress Wall sunset night view',
  },
  'food-seoul-everest-dongdaemun': {
    tag: '에베레스트동대문',
    displayHashtag: '#에베레스트',
    tiktokQuery: 'Everest restaurant Dongdaemun curry Seoul',
  },
  'food-seoul-kali-daehangno': {
    tag: '깔리대학로',
    displayHashtag: '#깔리',
    tiktokQuery: 'Kali Indian restaurant Hyehwa Daehangno Seoul',
  },
  'food-seoul-manakamana-daehangno': {
    tag: '머노까머나',
    displayHashtag: '#머노까머나',
    tiktokQuery: 'Manakamana Indian restaurant Hyehwa Daehangno Seoul',
  },
  'food-seoul-everest-curry-world-dongdaemun': {
    tag: '에베레스트커리월드',
    displayHashtag: '#에베레스트커리월드',
    tiktokQuery: 'Everest Curry World Dongdaemun Seoul',
  },
  'food-seoul-pooja-dongdaemun': {
    tag: '뿌자동대문',
    displayHashtag: '#뿌자',
    tiktokQuery: 'Pooja Indian restaurant Dongdaemun DDP Seoul',
  },
  'food-seoul-persian-palace-hyehwa': {
    tag: '페르시안궁전',
    displayHashtag: '#페르시안궁전',
    tiktokQuery: 'Persian Palace Hyehwa Seoul curry',
  },
  'seoul-ddp': {
    tag: '동대문디자인플라자',
    displayHashtag: '#DDP',
    tiktokQuery: 'DDP Dongdaemun Design Plaza Seoul',
  },
  'seoul-ikseondong': {
    tag: '익선동',
    displayHashtag: '#익선동',
    tiktokQuery: 'Ikseondong Hanok cafe street Seoul',
  },
  'seoul-seongsu-cafe-street': {
    tag: '성수동카페',
    displayHashtag: '#성수동카페',
    tiktokQuery: 'Seongsu cafe street Seoul Brooklyn',
  },
  'seoul-hongdae-street': {
    tag: '홍대',
    displayHashtag: '#홍대',
    tiktokQuery: 'Hongdae street Seoul nightlife busking',
  },
  'seoul-yeouido-hangang': {
    tag: '여의도한강공원',
    displayHashtag: '#여의도한강공원',
    tiktokQuery: 'Yeouido Hangang park ramen picnic Seoul',
  },
  'busan-haeundae-beach': {
    tag: '해운대',
    displayHashtag: '#해운대',
    tiktokQuery: 'Haeundae beach Busan',
  },
  'busan-gamcheon-culture-village': {
    tag: '감천문화마을',
    displayHashtag: '#감천문화마을',
    tiktokQuery: 'Gamcheon Culture Village Busan',
  },
  'busan-blue-line-sky-capsule': {
    tag: '스카이캡슐',
    displayHashtag: '#스카이캡슐',
    tiktokQuery: 'Haeundae Blue Line Park Sky Capsule Busan',
  },
  'jeju-seongsan-ilchulbong': {
    tag: '성산일출봉',
    displayHashtag: '#성산일출봉',
    tiktokQuery: 'Seongsan Ilchulbong sunrise peak Jeju',
  },
  'shopping-seoul-ipark-mall-yongsan': {
    tag: '아이파크몰용산',
    displayHashtag: '#아이파크몰용산',
    tiktokQuery: 'I\'Park Mall Yongsan fashion park sports sneaker',
  },
  'shopping-seoul-hyundai-city-outlets-dongdaemun': {
    tag: '현대시티아울렛동대문',
    displayHashtag: '#현대시티아울렛',
    tiktokQuery: 'Hyundai City Outlets Dongdaemun sneakers outlet discount',
  },
  'shopping-seoul-shinsegae-gangnam-central-city': {
    tag: '신세계강남스니커즈',
    displayHashtag: '#신세계강남',
    tiktokQuery: 'Shinsegae Gangnam sneaker sports department Seoul',
  },
  'shopping-seoul-myeongdong-sneakers-noon-square': {
    tag: '명동ABC마트그랜드스테이지',
    displayHashtag: '#ABC마트그랜드스테이지',
    tiktokQuery: 'Myeongdong ABC Mart Grand Stage shoe shopping Adidas Asics',
  },  'event-seoul-silver-grass-festival': {
    tag: '하늘공원억새축제',
    displayHashtag: '#하늘공원억새축제',
    tiktokQuery: 'Seoul Silver Grass Festival Haneul Park sunset',
  },
  'event-the-hyundai-christmas-village': {
    tag: '더현대서울크리스마스',
    displayHashtag: '#더현대서울크리스마스',
    tiktokQuery: 'The Hyundai Seoul Christmas village Sounds Forest',
  },
  'event-seoul-lantern-festival': {
    tag: '서울빛초롱축제',
    displayHashtag: '#서울빛초롱축제',
    tiktokQuery: 'Seoul Lantern Festival Cheonggyecheon Gwanghwamun',
  },
  'event-seoul-light-ddp-winter': {
    tag: '서울라이트',
    displayHashtag: '#서울라이트DDP',
    tiktokQuery: 'Seoul Light DDP winter media facade Dongdaemun',
  },
  'event-seoul-plaza-ice-skating': {
    tag: '서울광장스케이트장',
    displayHashtag: '#서울광장스케이트장',
    tiktokQuery: 'Seoul Plaza ice skating rink City Hall winter',
  },
  'event-show-myeongdong-nanta': {
    tag: '명동난타',
    displayHashtag: '#난타',
    tiktokQuery: 'Myeongdong Nanta show live performance Seoul',
  },
  'event-show-the-painters-gwanghwamun': {
    tag: '페인터즈',
    displayHashtag: '#페인터즈',
    tiktokQuery: 'The Painters live art show Seoul performance',
  },
  'event-bosingak-new-year-countdown': {
    tag: '보신각제야의종',
    displayHashtag: '#보신각타종행사',
    tiktokQuery: 'Bosingak bell ringing New Year countdown Seoul',
  },

};

/**
 * Generates live TikTok & Instagram exploration links for Korean sights.
 * Prioritizes verified viral Korean tags and concise names so users always find rich content.
 */
export function getSpotSocialLinks(
  nameKr: string,
  name?: string,
  spotId?: string,
  explicitInstagramTag?: string,
  explicitInstagramUrl?: string,
  explicitTiktokQuery?: string
): SpotSocialLinks {
  // 1. Direct explicit parameters from spot
  if (explicitInstagramUrl || explicitInstagramTag) {
    const cleanKrForSearch = (nameKr || '')
      .replace(/\s*\([^)]*\)/g, '')
      .split(/\s*[–-]\s*/)[0]
      .split('/')[0]
      .split('&')[0]
      .trim();
    const cleanEngForSearch = (name || '')
      .replace(/\s*\([^)]*\)/g, '')
      .split(/\s*[–-]\s*/)[0]
      .split(' & ')[0]
      .split(' / ')[0]
      .trim();
    const cleanTag = (explicitInstagramTag || cleanKrForSearch.replace(/\s+/g, '') || 'Korea').replace(/^#/, '');
    return {
      hashtag: `#${cleanTag}`,
      instagramUrl: explicitInstagramUrl || `https://www.instagram.com/explore/tags/${encodeURIComponent(cleanTag)}/`,
      tiktokUrl: `https://www.tiktok.com/search?q=${encodeURIComponent(explicitTiktokQuery || `${cleanEngForSearch || cleanKrForSearch} Korea`)}`,
      naverBlogUrl: `https://m.search.naver.com/search.naver?where=m_blog&query=${encodeURIComponent(explicitInstagramTag || cleanKrForSearch || cleanEngForSearch)}`,
    };
  }

  // 2. Check curated spot socials dictionary by ID
  if (spotId && CURATED_SPOT_SOCIALS[spotId]) {
    const c = CURATED_SPOT_SOCIALS[spotId];
    return {
      hashtag: c.displayHashtag,
      instagramUrl: c.instagramUrl || `https://www.instagram.com/explore/tags/${encodeURIComponent(c.tag)}/`,
      tiktokUrl: `https://www.tiktok.com/search?q=${encodeURIComponent(c.tiktokQuery || `${c.tag} Korea`)}`,
      naverBlogUrl: `https://m.search.naver.com/search.naver?where=m_blog&query=${encodeURIComponent(c.tag)}`,
    };
  }

  // 3. Check curated dictionary by nameKr match
  for (const [, c] of Object.entries(CURATED_SPOT_SOCIALS)) {
    if (nameKr && (nameKr.includes(c.tag) || c.tag.includes(nameKr))) {
      return {
        hashtag: c.displayHashtag,
        instagramUrl: c.instagramUrl || `https://www.instagram.com/explore/tags/${encodeURIComponent(c.tag)}/`,
        tiktokUrl: `https://www.tiktok.com/search?q=${encodeURIComponent(c.tiktokQuery || `${c.tag} Korea`)}`,
        naverBlogUrl: `https://m.search.naver.com/search.naver?where=m_blog&query=${encodeURIComponent(c.tag)}`,
      };
    }
  }

  // 4. Intelligent Fallback Tag Generator
  // Clean Korean landmark title (strip parentheses, branch descriptors like '...점', '...본점')
  const cleanKr = (nameKr || '')
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\s*(점|본점|[0-9]+호점)$/, '')
    .split('/')[0]
    .split('&')[0]
    .trim();

  const krWords = cleanKr.split(/\s+/).filter(Boolean);
  const primaryKrWord = krWords.length > 0 ? krWords[0] : '';
  const pureKr = cleanKr.replace(/[^\uAC00-\uD7A30-9]/g, '');

  // Extract concise English name (max 2 words, no long concatenated sentences)
  const cleanEng = (name || '')
    .replace(/\s*\([^)]*\)/g, '')
    .split(' - ')[0]
    .split(' & ')[0]
    .split(' / ')[0]
    .trim() || 'Korea Travel';

  const engWords = cleanEng.split(/\s+/).filter(w => !['and', '&', 'the', 'of', 'in', 'at'].includes(w.toLowerCase()));
  const shortEng = engWords.slice(0, 2).join('');
  const cleanEngTag = shortEng.replace(/[^a-zA-Z0-9]/g, '');

  // IN KOREA: Korean hashtags are 100x richer than English concatenations on Instagram.
  // Use Korean tag if valid (>= 2 Hangul characters), otherwise concise English tag.
  let targetTag = pureKr && pureKr.length >= 2 ? pureKr : cleanEngTag.toLowerCase();
  let displayTag = pureKr && pureKr.length >= 2 ? `#${pureKr}` : `#${cleanEngTag || 'KoreaTravel'}`;

  // If Korean tag is still long (> 10 chars), prefer primary brand word
  if (pureKr.length > 10 && primaryKrWord && primaryKrWord.length >= 2) {
    const compactKr = primaryKrWord.replace(/[^\uAC00-\uD7A30-9]/g, '');
    if (compactKr.length >= 2) {
      targetTag = compactKr;
      displayTag = `#${compactKr}`;
    }
  }

  const instagramUrl = `https://www.instagram.com/explore/tags/${encodeURIComponent(targetTag)}/`;
  const tiktokQuery = `${cleanEng} Korea`;
  const tiktokUrl = `https://www.tiktok.com/search?q=${encodeURIComponent(tiktokQuery)}`;
  const naverBlogUrl = `https://m.search.naver.com/search.naver?where=m_blog&query=${encodeURIComponent(cleanKr || cleanEng)}`;

  return {
    hashtag: displayTag,
    instagramUrl,
    tiktokUrl,
    naverBlogUrl,
  };
}

// In-Memory L1 Cache
const memoryImageCache = new Map<string, string>();
// In-Flight Promise Cache to deduplicate concurrent requests for the exact same spot
const inFlightRequests = new Map<string, Promise<string>>();

// ─── Concurrency Limiter Queue ───
class ConcurrencyQueue {
  private activeCount = 0;
  private queue: Array<() => void> = [];
  private maxConcurrency: number;

  constructor(maxConcurrency: number = MAX_CONCURRENT_REQUESTS) {
    this.maxConcurrency = maxConcurrency;
  }

  async run<T>(fn: () => Promise<T>): Promise<T> {
    if (this.activeCount >= this.maxConcurrency) {
      await new Promise<void>((resolve) => this.queue.push(resolve));
    }
    this.activeCount++;
    try {
      return await fn();
    } finally {
      this.activeCount--;
      if (this.queue.length > 0) {
        const next = this.queue.shift();
        next?.();
      }
    }
  }
}

export const requestQueue = new ConcurrencyQueue(MAX_CONCURRENT_REQUESTS);

// ─── Helper: Fetch With Timeout ───
export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = FETCH_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timer);
  }
}

// ─── Helper: Haversine Distance ───
export function haversineDistanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // Radius of Earth in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ─── Helper: Bounding Box from Point & Radius ───
export function bboxFromPoint(
  lat: number,
  lng: number,
  radiusMeters: number
): { minLng: number; minLat: number; maxLng: number; maxLat: number; bboxString: string } {
  const latDelta = radiusMeters / 111320;
  const lngDelta = radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180));
  const minLat = Number((lat - latDelta).toFixed(6));
  const maxLat = Number((lat + latDelta).toFixed(6));
  const minLng = Number((lng - lngDelta).toFixed(6));
  const maxLng = Number((lng + lngDelta).toFixed(6));

  return {
    minLng,
    minLat,
    maxLng,
    maxLat,
    bboxString: `${minLng},${minLat},${maxLng},${maxLat}`,
  };
}

// ─── Helper: Clean Search Terms ───
function cleanSpotSearchTerm(rawName: string): string {
  if (!rawName) return '';
  return rawName
    .replace(/\(.*?\)/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ─── Step 1: Wikidata & Wikipedia Image Lookup ───
/**
 * Searches Wikidata entities by name, validates coordinates with P625 (radius <= 2km),
 * resolves Wikimedia Commons P18 image URL, or falls back to Wikipedia REST summary API.
 */
export async function getWikiImage(
  name: string,
  lat?: number,
  lng?: number
): Promise<string | null> {
  const cleanName = cleanSpotSearchTerm(name);
  if (!cleanName) return null;

  try {
    // 1. Search Wikidata Entity by name (Korean first)
    const searchUrl = `https://www.wikidata.org/w/api.php?action=wbsearchentities&search=${encodeURIComponent(
      cleanName
    )}&language=ko&format=json&origin=*`;

    const searchRes = await fetchWithTimeout(searchUrl);
    if (searchRes.ok) {
      const searchData = await searchRes.json();
      const entities = searchData.search;

      if (Array.isArray(entities) && entities.length > 0) {
        // Inspect top candidate entity IDs (up to 4)
        const topEntityIds = entities.slice(0, 4).map((e: { id: string }) => e.id);
        const idsParam = topEntityIds.map(encodeURIComponent).join('|');

        const detailsUrl = `https://www.wikidata.org/w/api.php?action=wbgetentities&ids=${idsParam}&props=claims&format=json&origin=*`;
        const detailsRes = await fetchWithTimeout(detailsUrl);

        if (detailsRes.ok) {
          const detailsData = await detailsRes.json();
          const entitiesMap = detailsData.entities || {};

          for (const entityId of topEntityIds) {
            const entity = entitiesMap[entityId];
            if (!entity || !entity.claims) continue;

            // Check P625 (Coordinates) if spot lat/lng are provided
            if (typeof lat === 'number' && typeof lng === 'number') {
              const p625 = entity.claims.P625?.[0]?.mainsnak?.datavalue?.value;
              if (
                p625 &&
                typeof p625.latitude === 'number' &&
                typeof p625.longitude === 'number'
              ) {
                const distance = haversineDistanceMeters(
                  lat,
                  lng,
                  p625.latitude,
                  p625.longitude
                );
                if (distance > WIKIDATA_MAX_COORDINATE_DISTANCE_METERS) {
                  // Coordinate mismatch -> Skip to prevent false positives
                  continue;
                }
              }
            }

            // Check P18 (Image filename)
            const p18 = entity.claims.P18?.[0]?.mainsnak?.datavalue?.value;
            if (typeof p18 === 'string' && p18.trim()) {
              const commonsFileName = p18.trim();
              const commonsUrl = `https://commons.wikimedia.org/w/api.php?action=query&titles=File:${encodeURIComponent(
                commonsFileName
              )}&prop=imageinfo&iiprop=url&format=json&origin=*`;

              const commonsRes = await fetchWithTimeout(commonsUrl);
              if (commonsRes.ok) {
                const commonsData = await commonsRes.json();
                const pages = commonsData.query?.pages;
                if (pages) {
                  for (const pageId of Object.keys(pages)) {
                    const info = pages[pageId]?.imageinfo;
                    if (Array.isArray(info) && info[0]?.url) {
                      return info[0].url;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  } catch (err) {
    // Silently proceed to Wikipedia REST fallback
    console.debug('Wikidata search attempt failed:', err);
  }

  // 2. Fallback: Query Korean Wikipedia REST Summary API directly by title
  try {
    const wikiRestUrl = `https://ko.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(
      cleanName
    )}`;
    const wikiRes = await fetchWithTimeout(wikiRestUrl);
    if (wikiRes.ok) {
      const summary = await wikiRes.json();
      if (summary.thumbnail?.source) {
        return summary.thumbnail.source;
      }
      if (summary.originalimage?.source) {
        return summary.originalimage.source;
      }
    }
  } catch (err) {
    console.debug('Korean Wikipedia summary failed:', err);
  }

  // 3. Optional fallback for English Wikipedia summary if name has Latin letters
  if (/[a-zA-Z]/.test(cleanName)) {
    try {
      const enWikiRestUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(
        cleanName
      )}`;
      const enRes = await fetchWithTimeout(enWikiRestUrl);
      if (enRes.ok) {
        const enSummary = await enRes.json();
        if (enSummary.thumbnail?.source) {
          return enSummary.thumbnail.source;
        }
        if (enSummary.originalimage?.source) {
          return enSummary.originalimage.source;
        }
      }
    } catch {
      // Ignored
    }
  }

  return null;
}

// ─── Step 2: Mapillary Fallback ───
/**
 * Queries Mapillary Graph API v4 for street-level / crowd-sourced photos within radius.
 * Automatically sorts by distance to target point and retries once with doubled radius if needed.
 */
export async function getMapillaryImage(
  lat: number,
  lng: number,
  radiusMeters: number = 100
): Promise<string | null> {
  const token =
    (typeof import.meta !== 'undefined' &&
      import.meta.env &&
      import.meta.env.VITE_MAPILLARY_ACCESS_TOKEN) ||
    (typeof window !== 'undefined' && (window as unknown as { MAPILLARY_ACCESS_TOKEN?: string }).MAPILLARY_ACCESS_TOKEN) ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('mapillary_access_token')) ||
    '';

  if (!token || typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }

  const queryMapillary = async (currentRadius: number): Promise<string | null> => {
    try {
      const bbox = bboxFromPoint(lat, lng, currentRadius);
      const url = `https://graph.mapillary.com/images?access_token=${encodeURIComponent(
        token
      )}&fields=id,thumb_1024_url,thumb_2048_url,thumb_original_url,geometry&bbox=${
        bbox.bboxString
      }&limit=25`;

      const response = await fetchWithTimeout(url);
      if (!response.ok) return null;

      const data = await response.json();
      const images: Array<{
        id: string;
        thumb_1024_url?: string;
        thumb_2048_url?: string;
        thumb_original_url?: string;
        geometry?: { coordinates: [number, number] };
      }> = data.data || [];

      if (images.length === 0) return null;

      // Sort by distance to exact spot coordinates
      const sorted = images
        .filter((img) => img.geometry?.coordinates && Array.isArray(img.geometry.coordinates))
        .map((img) => {
          const [imgLng, imgLat] = img.geometry!.coordinates;
          const dist = haversineDistanceMeters(lat, lng, imgLat, imgLng);
          return { img, dist };
        })
        .sort((a, b) => a.dist - b.dist);

      if (sorted.length > 0) {
        const closest = sorted[0].img;
        return (
          closest.thumb_1024_url ||
          closest.thumb_2048_url ||
          closest.thumb_original_url ||
          null
        );
      }
      return null;
    } catch (err) {
      console.debug('Mapillary query error:', err);
      return null;
    }
  };

  // First attempt with base radius
  let result = await queryMapillary(radiusMeters);

  // If no image found, double radius once (up to max 300m) and retry
  if (!result && radiusMeters < 300) {
    const expandedRadius = Math.min(300, radiusMeters * 2);
    result = await queryMapillary(expandedRadius);
  }

  return result;
}

// ─── Step 3: Category Placeholder ───
/**
 * Maps a spot category to an aesthetic local SVG placeholder.
 * Handles both the requested generic categories and the app's SpotCategory types.
 */
export function getCategoryPlaceholder(category?: string | SpotCategory): string {
  if (!category) return CATEGORY_PLACEHOLDERS.default;

  const normalized = category.toLowerCase().trim();

  // Direct mapping
  if (CATEGORY_PLACEHOLDERS[normalized]) {
    return CATEGORY_PLACEHOLDERS[normalized];
  }

  // App-specific SpotCategory mapping
  switch (normalized) {
    case 'palace_heritage':
    case 'temple':
    case 'palace':
    case 'historic':
      return CATEGORY_PLACEHOLDERS.temple;

    case 'nature_viewpoint':
    case 'night_view':
    case 'viewpoint':
    case 'nature':
      return CATEGORY_PLACEHOLDERS.viewpoint;

    case 'photo_spot':
    case 'instagram':
      return CATEGORY_PLACEHOLDERS.viewpoint;

    case 'vegetarian_food':
    case 'cafe':
    case 'restaurant':
    case 'food':
    case 'dining':
      return CATEGORY_PLACEHOLDERS.cafe;

    case 'park':
    case 'garden':
      return CATEGORY_PLACEHOLDERS.park;

    case 'cultural_hidden':
    case 'modern_architecture':
    case 'museum':
    case 'culture':
      return CATEGORY_PLACEHOLDERS.museum;

    case 'activity':
    case 'activities':
    case 'sports':
    case 'leisure':
    case 'fitness':
    case 'gym':
    case 'workout':
      return CATEGORY_PLACEHOLDERS.activity;

    case 'shopping':
    case 'mall':
    case 'market':
    case 'pharmacy':
      return CATEGORY_PLACEHOLDERS.default;

    case 'charging_spot':
    case 'charging':
      return CATEGORY_PLACEHOLDERS.default;

    default:
      return CATEGORY_PLACEHOLDERS.default;
  }
}

// ─── Step 4: Orchestration & Caching ───
export interface SpotImageTarget {
  id?: string;
  name: string;
  nameKr?: string;
  lat: number;
  lng: number;
  category?: SpotCategory | string;
  imageUrl?: string;
}

/**
 * Resolves a real photo URL for a spot with full fallback chain:
 * 1. Wikidata / Wikipedia
 * 2. Mapillary
 * 3. Category Placeholder
 *
 * Caches results in L1 Memory and L2 LocalStorage.
 */
/**
 * Synchronously returns the best available image URL for thumbnails and cards
 * without triggering a 404 on non-existent local files.
 */
export function getImmediateSpotImageUrl(spot: SpotImageTarget): string {
  if (spot.imageUrl && spot.imageUrl.startsWith('/spots/')) {
    return spot.imageUrl;
  }
  const curated = getCuratedAuthenticPhoto(spot.nameKr, spot.name);
  if (curated) {
    return curated;
  }
  if (spot.imageUrl && !spot.imageUrl.includes('unsplash.com/photo-1538485399081')) {
    return spot.imageUrl;
  }
  return getCategoryPlaceholder(spot.category);
}

export async function resolveSpotImage(spot: SpotImageTarget): Promise<string> {
  const cacheKey = `spot-image:${spot.id || `${spot.lat.toFixed(4)},${spot.lng.toFixed(4)}`}`;

  // 1. Primary Fast Path: Dedicated local authentic spot photo
  if (spot.imageUrl && spot.imageUrl.startsWith('/spots/')) {
    memoryImageCache.set(cacheKey, spot.imageUrl);
    return spot.imageUrl;
  }

  // 2. Check L1 Memory Cache
  if (memoryImageCache.has(cacheKey)) {
    return memoryImageCache.get(cacheKey)!;
  }

  // 3. Check L2 LocalStorage Cache
  try {
    if (typeof localStorage !== 'undefined') {
      const cached = localStorage.getItem(cacheKey);
      if (cached && !cached.includes('unsplash.com/photo-1538485399081')) {
        memoryImageCache.set(cacheKey, cached);
        return cached;
      }
    }
  } catch {
    // LocalStorage might be disabled or full
  }

  // 4. Fast Path: Check Curated Authentic Photography (Instant 0ms)
  const curated = getCuratedAuthenticPhoto(spot.nameKr, spot.name);
  if (curated) {
    memoryImageCache.set(cacheKey, curated);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(cacheKey, curated);
      }
    } catch {}
    return curated;
  }

  // 4. Fast Path: Check if spot already has a verified specific image
  if (spot.imageUrl && !spot.imageUrl.includes('unsplash.com/photo-1538485399081')) {
    memoryImageCache.set(cacheKey, spot.imageUrl);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(cacheKey, spot.imageUrl);
      }
    } catch {}
    return spot.imageUrl;
  }

  // 5. Deduplicate in-flight requests for dynamic spots (Kakao / Overpass)
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)!;
  }

  const resolvePromise = (async () => {
    // Process through concurrency queue to respect Wikimedia rate limits
    return requestQueue.run(async () => {
      let resolvedUrl: string | null = null;

      // 1. Try Wikidata / Wikipedia with Hangul name first
      if (spot.nameKr) {
        resolvedUrl = await getWikiImage(spot.nameKr, spot.lat, spot.lng);
      }

      // If null and English/alternate name exists, try again
      if (!resolvedUrl && spot.name && spot.name !== spot.nameKr) {
        resolvedUrl = await getWikiImage(spot.name, spot.lat, spot.lng);
      }

      // 2. Try Mapillary near coordinates
      if (!resolvedUrl && typeof spot.lat === 'number' && typeof spot.lng === 'number') {
        resolvedUrl = await getMapillaryImage(spot.lat, spot.lng, 100);
      }

      // 3. Fallback to Category Placeholder
      const finalUrl = resolvedUrl || getCategoryPlaceholder(spot.category);

      // Save in caches
      memoryImageCache.set(cacheKey, finalUrl);
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(cacheKey, finalUrl);
        }
      } catch {
        // LocalStorage error handled
      }

      return finalUrl;
    });
  })();

  inFlightRequests.set(cacheKey, resolvePromise);

  try {
    const result = await resolvePromise;
    return result;
  } finally {
    inFlightRequests.delete(cacheKey);
  }
}

/**
 * Helper to clear spot image cache
 */
export function clearSpotImageCache(): void {
  memoryImageCache.clear();
  inFlightRequests.clear();
  try {
    if (typeof localStorage !== 'undefined') {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('spot-image:')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    }
  } catch {}
}
